/**
 * Privacy Governance Types
 * PROJ-082 - Privacy Governance Canvas Integration
 */

export type LegalBasis =
  | 'consent'
  | 'contract'
  | 'legal_obligation'
  | 'vital_interest'
  | 'public_interest'
  | 'legitimate_interest'
  | 'research'
  | 'health'
  | 'credit_protection'
  | 'public_policy'
  | 'regulatory';

export type CrossBorderMechanism =
  | 'adequacy'
  | 'scc'
  | 'bcr'
  | 'certification'
  | 'consent';

export type DpiaStatus =
  | 'not_required'
  | 'pending'
  | 'in_progress'
  | 'completed';

export type RopaFormat =
  | 'gdpr_art30'
  | 'chile_apdp'
  | 'lgpd_anpd'
  | 'generic'
  | 'csv'
  | 'json';

export interface NodePrivacyGovernance {
  nodeId: string;
  purpose?: string;
  legalBasis?: LegalBasis;
  retentionDays?: number;
  isCrossBorder?: boolean;
  crossBorderCountry?: string;
  crossBorderMechanism?: CrossBorderMechanism;
  dpiaRequired?: boolean;
  dpiaStatus?: DpiaStatus;
  dpiaDocumentId?: string;
  dpoUserId?: string;
  applicableLawIds?: string[];
  complianceScore?: number;
  dataAttributes?: NodeDataAttributeInfo[];
}

export interface NodeDataAttributeInfo {
  code: string;
  name: string;
  category: string;
  classificationCode?: string;
  classificationLevel?: string;
  classificationColor?: string;
}

export interface DpiaAssessment {
  required: boolean;
  reasons: string[];
  triggeringLaws: Array<{ id: string; code: string; name: string }>;
}

export interface FlowComplianceSummary {
  flowId: string;
  totalNodes: number;
  nodesWithGovernance: number;
  overallScore: number;
  scoresByLaw: Record<string, number>;
  dpiaRequired: number;
  dpiaCompleted: number;
  crossBorderNodes: number;
}

export interface ComplianceScoreBreakdown {
  total: number;
  breakdown: {
    classification: number;
    purpose: number;
    legalBasis: number;
    retention: number;
    attributesClassified: number;
    dpia: number;
    laws: number;
  };
  penalties: {
    dpiaNotCompleted: number;
    crossBorderNoMechanism: number;
  };
}

export interface ApplicableLaw {
  id: string;
  code: string;
  name: string;
  country?: string;
  requiresDpia?: boolean;
  requiresRopa?: boolean;
  requiresDpo?: boolean;
  requiresLegalBasis?: boolean;
  requiresRetentionPolicy?: boolean;
  legalBasisOptions?: string[];
}

export interface ApplicableLawsResponse {
  explicit: ApplicableLaw[];
  suggested: ApplicableLaw[];
}

// ============================================================================
// Report Types (FASE 6)
// ============================================================================

export interface ReportData {
  format: RopaFormat;
  generatedAt: string;
  flowName: string;
  flowId: string;
  totalNodes: number;
  columns: string[];
  rows: Record<string, string | number | boolean>[];
  csvContent?: string;
}

export const ROPA_FORMAT_LABELS: Record<RopaFormat, string> = {
  gdpr_art30: 'RGPD Art. 30 (RAT)',
  chile_apdp: 'Ley 21.719 Chile',
  lgpd_anpd: 'LGPD Brasil (RIPD)',
  generic: 'ISO 27701 Universal',
  csv: 'CSV',
  json: 'JSON',
};

export const ROPA_FORMAT_DESCRIPTIONS: Record<RopaFormat, string> = {
  gdpr_art30: 'Registro de Actividades de Tratamiento segun Articulo 30 del RGPD',
  chile_apdp: 'Formato requerido por la Ley 21.719 de Proteccion de Datos de Chile',
  lgpd_anpd: 'Relatorio de Impacto a Protecao de Dados para la ANPD de Brasil',
  generic: 'Formato universal compatible con ISO 27701 (union de todas las columnas)',
  csv: 'Formato CSV para importar en hojas de calculo',
  json: 'Datos estructurados JSON para integracion',
};

// Display helpers

export const LEGAL_BASIS_LABELS: Record<LegalBasis, string> = {
  consent: 'Consentimiento',
  contract: 'Ejecucion de Contrato',
  legal_obligation: 'Obligacion Legal',
  vital_interest: 'Interes Vital',
  public_interest: 'Interes Publico',
  legitimate_interest: 'Interes Legitimo',
  research: 'Investigacion',
  health: 'Salud Publica',
  credit_protection: 'Proteccion Crediticia',
  public_policy: 'Politica Publica',
  regulatory: 'Cumplimiento Regulatorio',
};

export const CROSS_BORDER_MECHANISM_LABELS: Record<CrossBorderMechanism, string> = {
  adequacy: 'Decision de Adecuacion',
  scc: 'Clausulas Contractuales Tipo (SCC)',
  bcr: 'Normas Corporativas Vinculantes (BCR)',
  certification: 'Certificacion',
  consent: 'Consentimiento Explicito',
};

export const DPIA_STATUS_LABELS: Record<DpiaStatus, string> = {
  not_required: 'No Requerida',
  pending: 'Pendiente',
  in_progress: 'En Progreso',
  completed: 'Completada',
};

export const DPIA_STATUS_COLORS: Record<DpiaStatus, string> = {
  not_required: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
};

/** Country code to flag emoji mapping for common privacy law jurisdictions */
export const COUNTRY_FLAGS: Record<string, string> = {
  EU: '\uD83C\uDDEA\uD83C\uDDFA',
  CL: '\uD83C\uDDE8\uD83C\uDDF1',
  BR: '\uD83C\uDDE7\uD83C\uDDF7',
  US: '\uD83C\uDDFA\uD83C\uDDF8',
  AR: '\uD83C\uDDE6\uD83C\uDDF7',
  MX: '\uD83C\uDDF2\uD83C\uDDFD',
  CO: '\uD83C\uDDE8\uD83C\uDDF4',
  PE: '\uD83C\uDDF5\uD83C\uDDEA',
  UY: '\uD83C\uDDFA\uD83C\uDDFE',
  GB: '\uD83C\uDDEC\uD83C\uDDE7',
  CA: '\uD83C\uDDE8\uD83C\uDDE6',
  JP: '\uD83C\uDDEF\uD83C\uDDF5',
  KR: '\uD83C\uDDF0\uD83C\uDDF7',
  AU: '\uD83C\uDDE6\uD83C\uDDFA',
};
